import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StbButton from '../security-toolbox-ui/StbButton';
import { useLabScenarios } from '../../hooks/lab-hooks';
import { LabScenario, labScenarioCustomer } from '../../support/labService';
import LabScenarioEditorModal from './LabScenarioEditorModal';
import { LabCard, LabPanelState, LabStatusTag } from './LabShared';

export type LabScenarioListProps = {
  /** Where a scenario opens. Defaults to the lab area's own detail route. */
  onOpen?: (scenario: LabScenario) => void;
  /** Authoring. Reading the catalogue is not gated; changing what pods will be asked to do is. */
  canEdit?: boolean;
};

/**
 * The scenario catalogue.
 *
 * Scenarios are authored content, so this reads from the generic CRUD API and not the lab
 * controller. Counts come from the nested children the list serializer returns; a scenario
 * with no steps is called out because it will open runs that finish immediately.
 */
const LabScenarioList: React.FC<LabScenarioListProps> = ({ onOpen, canEdit = false }) => {
  const navigate = useNavigate();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<LabScenario | null>(null);

  const { data: scenarios, isLoading, error, refetch } = useLabScenarios();

  const open = (scenario: LabScenario) => {
    if (onOpen) {
      onOpen(scenario);
      return;
    }

    navigate(`/lab/scenarios/${scenario.id}`);
  };

  return (
    <>
      <LabCard
        title="Lab scenarios"
        subtitle="What a pod is asked to do, how it is checked and what counts as a pass."
        actions={
          <>
            <StbButton role="default" size="small" onClick={async () => refetch()}>
              Refresh
            </StbButton>
            {canEdit && (
              <StbButton
                role="add"
                size="small"
                onClick={async () => {
                  setEditing(null);
                  setEditorOpen(true);
                }}
              >
                New scenario
              </StbButton>
            )}
          </>
        }
      >
        <LabPanelState
          loading={isLoading}
          error={error}
          empty={!scenarios?.length}
          emptyMessage="No scenarios yet."
          loadingLabel="Loading scenarios..."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Scenario</th>
                  <th className="px-3 py-2 font-semibold">Demo</th>
                  <th className="px-3 py-2 font-semibold">Content</th>
                  <th className="px-3 py-2 font-semibold">Pass at</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {(scenarios || []).map((scenario) => {
                  const stepCount = (scenario.steps || []).length;
                  const criterionCount = (scenario.success_criteria || []).length;
                  const deviceCount = (scenario.devices || []).length;
                  const customer = labScenarioCustomer(scenario);

                  return (
                    <tr key={scenario.id} className="border-b border-gray-100 align-top">
                      <td className="px-3 py-2">
                        <div className="font-medium">{scenario.name}</div>
                        <div className="text-xs text-gray-500">{scenario.slug}</div>

                        <div className="mt-1 flex flex-wrap gap-1">
                          {!scenario.enabled && (
                            <LabStatusTag label="Disabled" sentiment="warning" />
                          )}
                          {scenario.auto_start && (
                            <LabStatusTag label="Auto-start" sentiment="info" />
                          )}
                          {scenario.bootstrap_only && (
                            <LabStatusTag label="Bootstrap only" sentiment="info" />
                          )}
                          {/* Every lab is set at PseudoCo. Anything else will not load. */}
                          {customer && customer !== 'PseudoCo' && (
                            <LabStatusTag label={`Customer: ${customer}`} sentiment="danger" />
                          )}
                        </div>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        {scenario.demo_uid ? (
                          <div>
                            <div>{scenario.demo_name || scenario.demo_uid}</div>
                            <div className="text-xs text-gray-500">{scenario.demo_uid}</div>
                          </div>
                        ) : (
                          <span className="text-gray-500">Not bound to a demo</span>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <div>{`${deviceCount} device${deviceCount === 1 ? '' : 's'}`}</div>
                        <div className="text-xs text-gray-500">
                          {`${stepCount} step${stepCount === 1 ? '' : 's'}, ${criterionCount} criteri${
                            criterionCount === 1 ? 'on' : 'a'
                          }`}
                        </div>
                        {!stepCount && (
                          <LabStatusTag label="No steps" sentiment="warning" />
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        {`${scenario.pass_threshold_percent}%`}
                      </td>

                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <StbButton role="request" size="xsmall" onClick={async () => open(scenario)}>
                            Open
                          </StbButton>
                          {canEdit && (
                            <StbButton
                              role="update"
                              size="xsmall"
                              onClick={async () => {
                                setEditing(scenario);
                                setEditorOpen(true);
                              }}
                            >
                              Edit
                            </StbButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </LabPanelState>
      </LabCard>

      <LabScenarioEditorModal
        open={editorOpen}
        scenario={editing}
        onClose={() => setEditorOpen(false)}
      />
    </>
  );
};

export default LabScenarioList;
